import React, { useEffect, useRef, useState } from "react";
import { Grid } from "components/Grid";
import ActionsBar from "components/SubHeader/ActionsBar";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { TCity, TProvince } from "types/dto";
import { toast } from "react-toastify";
import { AutoComplete } from "components/AutoComplete";
import { useURLTools } from "utils/URLtools";
import SubHeaderFilters from "components/SubHeader/SubHeadeFilters";
import CenterContainer from "components/CenterContainer";
import Spinner from "components/Spinner";
import { AddCityModal, TAddFormTypes } from "./AddCityModal";
import { EditCityModal, TEditFormTypes } from "./EditCityModal";
import { TResponseCode } from "utils/netCall/types";

const CitiesView = () => {
  const { t } = useTranslation();
  const { queryParams, addUpdateRemoveQueryParams } = useURLTools();
  const [provinces, setProvinces] = useState<TProvince[]>([]);
  const [cities, setCities] = useState<TCity[]>([]);
  const [isProvincesLoading, setIsProvincesLoading] = useState(false);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState<TProvince>();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<TCity>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const provinceGridRef = useRef(null);

  const onAddClick = () => {
    if (selectedProvince) {
      setAddModalOpen(true);
    } else {
      toast.warning(t("selectProvince"));
    }
  };
  const onEditClick = (data: any) => {
    if (selectedProvince) {
      setSelectedCity(data.row.data);
      setIsEditModalOpen(true);
    } else {
      toast.warning(t("selectProvince"));
    }
  };
  const getProvinceList = async () => {
    setIsProvincesLoading(true);
    const res = await netCall<TProvince[]>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/Province/GetAllProvince`,
    });
    if (res.responseCode === TResponseCode.OK) {
      setProvinces(res.results || []);
      setIsProvincesLoading(false);
    } else {
      setIsProvincesLoading(false);
      toast.warning(t("fetchDataError"));
    }
  };

  const getCitiesList = async (provinceId: string) => {
    setIsCitiesLoading(true);
    const res = await netCall<TProvince>({
      method: "GET",
      endPoint: `${ManagementApiUrl()}/api/Province/GetProvince/${provinceId}`,
    });
    if (res.responseCode === TResponseCode.OK) {
      setCities(res.results.cities || []);
      setIsCitiesLoading(false);
    } else {
      setIsCitiesLoading(false);
      toast.warning(t("fetchDataError"));
    }
  };

  const onSubmitAddCity = async (values: TAddFormTypes) => {
    if (selectedProvince) {
      setIsSubmitting(true);
      const res = await netCall<TProvince>({
        method: "POST",
        endPoint: `${ManagementApiUrl()}/api/City/AddCity`,
        body: {
          name: values.name,
          provinceId: selectedProvince.id,
        },
      });
      if (res.responseCode === TResponseCode.OK) {
        setIsSubmitting(false);
        toast.success(t("operationSuccessful"));
        getCitiesList(selectedProvince.id);
        setAddModalOpen(false);
      } else {
        setIsSubmitting(false);
        toast.error(t("operationFailed"));
      }
    }
  };
  const onSubmitEditCity = async (values: TEditFormTypes) => {
    if (selectedCity && selectedProvince) {
      setIsSubmitting(true);
      const res = await netCall<TCity>({
        method: "POST",
        endPoint: `${ManagementApiUrl()}/api/City/UpdateCity`,
        body: {
          id: selectedCity.id,
          name: values.name,
          provinceId: selectedProvince.id,
        },
      });
      if (res.responseCode === TResponseCode.OK) {
        setIsSubmitting(false);
        toast.success(t("operationSuccessful"));
        getCitiesList(selectedProvince.id);
        setIsEditModalOpen(false);
        setSelectedCity(undefined);
      } else {
        setIsSubmitting(false);
        toast.error(t("operationFailed"));
      }
    }
  };
  useEffect(() => {
    const provinceId = queryParams?.provinceId;
    if (provinceId && provinces) {
      setSelectedProvince(provinces.find((p) => p.id === provinceId));
      getCitiesList(provinceId);
    }
  }, [queryParams, provinces]);

  useEffect(() => {
    getProvinceList();
  }, []);

  return (
    <>
      <SubHeader>
        <SubHeaderTitle>{t("cities")}</SubHeaderTitle>
        <ActionsBar
          requestTime={0}
          isLoading={false}
          actionButtons={[
            {
              iconName: "MdAdd",
              buttonTooltip: "افزودن",
              action: onAddClick,
            },
          ]}
        />
      </SubHeader>
      <SubHeaderFilters>
        <AutoComplete
          defaultSelected={selectedProvince}
          onSelect={(value: any) => {
            setSelectedProvince(value);
            addUpdateRemoveQueryParams([
              { parameter: "provinceId", value: value?.id },
            ]);
          }}
          optionLabel="name"
          suggestions={provinces}
          placeholder={t("province")}
          hasLabel={false}
          className="w-48"
          onClear={() => {
            setSelectedProvince(undefined);
            addUpdateRemoveQueryParams([
              { parameter: "provinceId", value: "" },
            ]);
          }}
          loading={isProvincesLoading}
        />
      </SubHeaderFilters>

      <div className={"w-full flex gap-x h-full"}>
        {isCitiesLoading && (
          <div className="z-[200] absolute bg-mofidGray-40 backdrop-blur-[2px] bg-opacity-30 top-0 left-0 bottom-0 right-0 flex justify-center items-center rounded-md">
            <p>{t("gettingInformation")}</p>
            <Spinner />
          </div>
        )}
        {queryParams?.provinceId ? (
          <>
            {cities.length === 0 ? (
              <CenterContainer>
                <p className={"text-base"}>{t("noDataToShow")}</p>
              </CenterContainer>
            ) : (
              <Grid
                ref={provinceGridRef}
                gridKey={"id"}
                isLoading={isCitiesLoading}
                dataSource={cities}
                columns={[
                  {
                    dataField: "id",
                    caption: t("id"),
                  },
                  {
                    dataField: "name",
                    caption: t("name"),
                  },
                  {
                    caption: t("actions"),
                    visible: true,
                    width: 110,
                    buttons: [
                      {
                        icon: "fa-solid fa-pencil",
                        hint: t("edit"),
                        onClick: onEditClick,
                      },
                    ],
                  },
                ]}
                hasSearchPanel={false}
                hasPagination={true}
                hasGroupingFeatures={false}
                hasFilterBuilder={false}
              />
            )}
          </>
        ) : (
          <CenterContainer>
            <p className={"text-base"}>{t("pleaseSelectProvince")}</p>
          </CenterContainer>
        )}
      </div>
      {addModalOpen && (
        <AddCityModal
          onClose={() => setAddModalOpen(false)}
          isOpen={addModalOpen}
          onSubmit={onSubmitAddCity}
          province={selectedProvince!}
          isLoading={isSubmitting}
        />
      )}
      {isEditModalOpen && (
        <EditCityModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCity(undefined);
          }}
          onSubmit={onSubmitEditCity}
          isLoading={isSubmitting}
          record={selectedCity!}
          province={selectedProvince!}
        />
      )}
    </>
  );
};

export default CitiesView;
